import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Customer role can only access customer portal, not the main app
  if (user.role === 'customer') {
    return <Navigate to="/customer-portal/dashboard" replace />;
  }

  // Redirect to onboarding if not completed (allow onboarding and settings pages)
  // '/admin' is Admin & Settings — where Stripe, company details, automations
  // and the rest of setup actually live. Redirecting a still-onboarding user
  // AWAY from it sent them back to the wizard every time they tried to finish
  // the very things onboarding asks for.
  const onboardingExemptPaths = ['/onboarding', '/settings', '/admin', '/help', '/verify-email'];
  const isOnboardingExempt = onboardingExemptPaths.some(path => location.pathname.startsWith(path));

  if (user.onboarding_status && user.onboarding_status !== 'completed' && !isOnboardingExempt) {
    if (user.onboarding_status === 'pending') {
      return <Navigate to="/onboarding" replace />;
    }
    // 'in_progress' users can access dashboard but will see the onboarding banner
    // 'suspended' users get blocked by the backend middleware
  }

  return <>{children}</>;
};

export default PrivateRoute;
